import type { RootState } from 'redux/rootReducer';
import {
  differenceInMonths,
  endOfMonth,
  format,
  startOfMonth,
  startOfToday
} from 'date-fns';
import { differenceBy } from 'lodash';
import {
  call,
  put,
  SagaReturnType,
  select,
  take,
  takeEvery
} from 'redux-saga/effects';

import { UserPreferences } from './types';

import { formatISOString } from 'utils/date';
import handleGrants from 'utils/grants';
import { saveDojoPreference } from 'utils/migration';

import { getPlanUnits } from 'RestAPIs/cost-centers';
import { getEmployeeWorkAgreements } from 'RestAPIs/employees/work-agreements';
import { getHolidays } from 'RestAPIs/holidays';
import { getPlanGroups } from 'RestAPIs/plan-groups';
import { savePreference } from 'RestAPIs/preferences';

import {
  FetchPlanUnitsAction,
  setEmployeePlanUnits,
  setHolidays,
  setPlanGroups,
  setPlanUnits,
  setPublicPlanUnits,
  setUserGrants,
  setUserPreferences,
  setWorkAgreements,
  UserDetailsAction,
  UserPreferenceAction
} from './actionCreators';

export function* fetchPlanUnits() {
  const plannerIntervals = new Set();
  const employeeIntervals = new Set();
  const publicIntervals = new Set();

  yield takeEvery(
    'FETCH_PLANUNITS',
    function* ({ payload = {} }: FetchPlanUnitsAction) {
      const {
        interval,
        useEmployeePlanunits,
        usePublicPlanUnits,
        filterByType
      } = payload;

      const startDate = startOfMonth(interval?.startDate || startOfToday());
      const endDate = endOfMonth(interval?.endDate || startOfToday());
      const startDateInt = format(startDate, 'yyyy-MM');
      const endDateInt = format(endDate, 'yyyy-MM');
      const isSameMonth = startDateInt === endDateInt;
      const intervals = useEmployeePlanunits
        ? employeeIntervals
        : usePublicPlanUnits
        ? publicIntervals
        : plannerIntervals;
      const isStartDateIncluded = intervals.has(startDateInt);
      const isEndDateIncluded = intervals.has(endDateInt);

      if (!(isStartDateIncluded && isEndDateIncluded)) {
        const startDateParam = isSameMonth
          ? startDate
          : isStartDateIncluded
          ? startOfMonth(endDate)
          : startOfMonth(startDate);

        const endDateParam = isSameMonth
          ? endDate
          : isEndDateIncluded
          ? endOfMonth(startDate)
          : endOfMonth(endDate);

        const response: SagaReturnType<typeof getPlanUnits> = yield call(
          getPlanUnits,
          {
            filterByType,
            startDate: startDateParam,
            endDate: endDateParam,
            type: usePublicPlanUnits
              ? 'public'
              : useEmployeePlanunits
              ? 'employee'
              : undefined
          }
        );

        let newPlanunits = response.data;

        if (usePublicPlanUnits) {
          newPlanunits = newPlanunits.map((pu) => ({
            ...pu,
            id: pu.brId as number,
            dynId: pu.brDynId as number,
            validity: {
              start: pu.valid?.from as string,
              end: pu.valid?.to as string
            }
          }));
        }

        intervals.add(format(startDate, 'yyyy-MM'));

        while (differenceInMonths(startDate, endDate) < 0) {
          startDate.setMonth(startDate.getMonth() + 1);
          intervals.add(format(startDate, 'yyyy-MM'));
        }

        const state: RootState = yield select((state: RootState) => state);
        const existingPlanunits = useEmployeePlanunits
          ? state.employeePlanUnits
          : usePublicPlanUnits
          ? state.publicPlanUnits
          : state.planUnits;
        const diffPlanunits = differenceBy(
          newPlanunits,
          existingPlanunits,
          'dynId'
        );

        if (diffPlanunits.length) {
          // TODO: move .map((pu) => ({ ...pu, uniqId: pu.id })) to diffPlanunits after proper typing is implemented
          const planunits = [...existingPlanunits, ...diffPlanunits].map(
            (pu) => ({ ...pu, uniqId: pu.id })
          );

          yield put(
            useEmployeePlanunits
              ? setEmployeePlanUnits(planunits)
              : usePublicPlanUnits
              ? setPublicPlanUnits(planunits)
              : setPlanUnits(planunits)
          );

          const { planGroups }: RootState = yield select(
            (state: RootState) => state
          );
          if (
            planGroups.length &&
            !(useEmployeePlanunits || usePublicPlanUnits)
          ) {
            yield put(
              setPlanGroups(
                planGroups.map(({ costCenters, ...rest }) => ({
                  ...rest,
                  costCenters: costCenters
                    .map(
                      ({ id: _id }) =>
                        planunits.filter(({ id }) => id === _id)[0]
                    )
                    .filter((v) => v)
                }))
              )
            );
          }
        }
      }
    }
  );
}

export function* fetchPlanGroups() {
  yield take('FETCH_PLANGROUPS'); // one time fetch
  const response: SagaReturnType<typeof getPlanGroups> =
    yield call(getPlanGroups);
  const planGroups = response.data;
  const { planUnits }: RootState = yield select((state: RootState) => state);

  yield put(
    setPlanGroups(
      planGroups.map(({ costCenters, id, ...rest }) => ({
        ...rest,
        id,
        costCenters: planUnits.length
          ? costCenters
              .map(
                ({ id: ccId }) => planUnits.filter(({ id }) => id === ccId)[0]
              )
              .filter((v) => v)
          : costCenters,
        uniqId: `${id}-group`
      }))
    )
  );
}

export function* handlePreferences() {
  yield takeEvery(
    'SET_USER_DETAILS',
    function* ({ payload }: UserDetailsAction) {
      if (payload.preferences) {
        yield put(
          setUserPreferences(
            payload.preferences.reduce((o, { key, value }) => {
              try {
                o[key] = JSON.parse(value);
              } catch {
                o[key] = value;
              }
              return o;
            }, {} as UserPreferences)
          )
        );
      }
      yield put(
        setUserGrants(handleGrants(payload.grants || [], payload.accountId))
      );
    }
  );
}

export function* setPreference() {
  yield takeEvery(
    'SET_PREFERENCE',
    function* ({ payload }: UserPreferenceAction) {
      const { key, value, isDojoDispatch } = payload;
      if (!isDojoDispatch) {
        // Store strings RAW, matching the dojo PreferencesStore convention and
        // the URL/enum form; JSON.stringify would persist them with literal
        // quotes ('"roster-new"'). Non-strings keep their JSON encoding, which
        // is identical to what the dojo store writes for them.
        const serialized =
          typeof value === 'string' ? value : JSON.stringify(value);
        yield call(savePreference, [{ key, value: serialized }]);
        saveDojoPreference(key, value);
      }
      const { preferences }: RootState = yield select(
        (state: RootState) => state
      );
      const newPrefs = {
        ...preferences,
        [key]: value
      };
      // TODO: After migration is done, add preferences to sessionStorage and remove isDojoDispatch check
      // sessionStorage.setItem('userPreferences', JSON.stringify(newPrefs));
      yield put(setUserPreferences(newPrefs));
    }
  );
}

export function* fetchHolidays() {
  yield take('GET_HOLIDAYS'); // one time fetch
  const { data } = yield call(getHolidays);
  yield put(
    setHolidays(
      data.reduce((holidaysByDate, holiday) => {
        holidaysByDate[formatISOString(holiday.date, 'yyyy-MM-dd')] = holiday;
        return holidaysByDate;
      }, {})
    )
  );
}

export function* fetchWorkAgreements() {
  yield take('FETCH_WORK_AGREEMENTS'); // one time fetch
  const { data } = yield call(getEmployeeWorkAgreements, {
    startDate: startOfToday(),
    endDate: startOfToday()
  });
  yield put(setWorkAgreements(data));
}
